import React, { FC, useCallback, useMemo, useState } from "react";
import { LocalEditableGrid } from "@getregistered/greg-editors";
import type { Grid, GridRow } from "@getregistered/greg-editors";

interface Field {
  name: string;
  type: string;
  label?: string;
  required?: boolean;
  options?: string[];
  multiple?: boolean;
  max_files?: number;
  accept?: string[];
}

export interface FieldSchemaBuilderProps {
  fieldTypes: string[];
  initialFields: Field[];
  hiddenFieldId: string;
  heading?: string;
  description?: string;
  addLabel?: string;
  emptyHeading?: string;
  emptyDescription?: string;
}

let nextId = 1;

const DEFAULT_ACCEPT = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

const DEFAULT_MAX_FILES = 5;
const MAX_FILES_CEILING = 10;

function labelToName(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function fieldToRow(field: Field): GridRow {
  const optionsValue = (field.options ?? []).join(", ");
  const acceptValue = (field.accept ?? DEFAULT_ACCEPT).join(", ");
  const isFile = field.type === "file";
  const isSelect = field.type === "select";

  return {
    id: `field-${nextId++}`,
    label: {
      rawValue: field.label ?? "",
      displayValue: field.label ?? "",
      cellVariant: { type: "input" as const, placeholder: "Display name" },
    },
    field_type: {
      rawValue: field.type,
      displayValue: field.type,
      cellVariant: {
        type: "select" as const,
        options: [], // filled dynamically via grid column
      },
    },
    required: {
      rawValue: field.required ?? false,
      displayValue: field.required ? "Yes" : "No",
      cellVariant: { type: "checkbox_toggle" as const },
    },
    options: {
      rawValue: optionsValue,
      displayValue: optionsValue,
      cellVariant: {
        type: "input" as const,
        placeholder: "Comma-separated",
        disabled: !isSelect,
      },
    },
    multiple: {
      rawValue: field.multiple ?? false,
      displayValue: field.multiple ? "Yes" : "No",
      cellVariant: { type: "checkbox_toggle" as const, disabled: !isFile },
    },
    max_files: {
      rawValue: String(field.max_files ?? DEFAULT_MAX_FILES),
      displayValue: String(field.max_files ?? DEFAULT_MAX_FILES),
      cellVariant: { type: "input" as const, placeholder: "1-10", disabled: !isFile },
    },
    accept: {
      rawValue: acceptValue,
      displayValue: acceptValue,
      cellVariant: {
        type: "input" as const,
        placeholder: "Comma-separated MIME types",
        disabled: !isFile,
      },
    },
  };
}

function applyConditionalDisabled(rows: GridRow[]): GridRow[] {
  return rows.map((r) => {
    const type = String(extractRaw(r.field_type) || "");
    const isFile = type === "file";
    const isSelect = type === "select";

    return {
      ...r,
      options: setDisabled(r.options, !isSelect),
      multiple: setDisabled(r.multiple, !isFile),
      max_files: setDisabled(r.max_files, !isFile),
      accept: setDisabled(r.accept, !isFile),
    };
  });
}

function setDisabled(cell: unknown, disabled: boolean): unknown {
  const c = cell as { cellVariant?: { disabled?: boolean } } | undefined;
  if (!c || c.cellVariant?.disabled === disabled) return cell;
  return {
    ...(cell as object),
    cellVariant: { ...(c.cellVariant ?? {}), disabled },
  };
}

function rowToField(row: GridRow): Field | null {
  const label = String(extractRaw(row.label) || "").trim();
  if (!label) return null;

  const name = labelToName(label);
  if (!name) return null;

  const field: Field = {
    name,
    label,
    type: String(extractRaw(row.field_type) || "text"),
  };

  const required = extractRaw(row.required);
  if (required === true || required === "true") field.required = true;

  if (field.type === "select") {
    const raw = String(extractRaw(row.options) || "");
    const options = raw
      .split(",")
      .map((o) => o.trim())
      .filter(Boolean);
    if (options.length > 0) field.options = options;
  }

  if (field.type === "file") {
    const multiple = extractRaw(row.multiple);
    if (multiple === true || multiple === "true") field.multiple = true;

    const maxRaw = String(extractRaw(row.max_files) || "").trim();
    if (maxRaw) {
      const n = parseInt(maxRaw, 10);
      if (Number.isFinite(n)) {
        field.max_files = Math.min(Math.max(n, 1), MAX_FILES_CEILING);
      }
    }

    const acceptRaw = String(extractRaw(row.accept) || "");
    const accept = acceptRaw
      .split(",")
      .map((o) => o.trim())
      .filter(Boolean);
    if (accept.length > 0) field.accept = accept;
  }

  return field;
}

function extractRaw(cell: unknown): unknown {
  if (cell && typeof cell === "object" && "rawValue" in cell) {
    return (cell as { rawValue: unknown }).rawValue;
  }
  return cell;
}

export const FieldSchemaBuilder: FC<FieldSchemaBuilderProps> = ({
  fieldTypes,
  initialFields,
  hiddenFieldId,
  heading = "Field Schema",
  description = "Define additional custom fields for items of this type. Note: Name, Description, URL, and Image are built-in fields and do not need to be added here.",
  addLabel = "Add Field",
  emptyHeading = "No fields defined",
  emptyDescription = "Add fields to define the schema for items of this type.",
}) => {
  const typeOptions = fieldTypes.map((t) => ({
    value: t,
    label: t.charAt(0).toUpperCase() + t.slice(1),
  }));

  const grid: Grid = {
    columns: [
      { key: "label", label: "Label", noSort: true, noFilter: true, description: "The display name shown to users. A unique key is auto-generated from this." },
      { key: "field_type", label: "Type", noSort: true, noFilter: true, description: "The input type for this field (e.g. text, email, url, file)." },
      { key: "required", label: "Required", noSort: true, noFilter: true, description: "When checked, this field must be filled in before submitting." },
      { key: "options", label: "Options", noSort: true, noFilter: true, description: "Comma-separated list of choices. Used only for Select fields." },
      { key: "multiple", label: "Multiple", noSort: true, noFilter: true, description: "Allow uploading more than one file. File fields only." },
      { key: "max_files", label: "Max files", noSort: true, noFilter: true, description: "Maximum files (1-10). File fields with Multiple enabled." },
      { key: "accept", label: "Accept", noSort: true, noFilter: true, description: "Comma-separated MIME types. File fields only." },
    ],
    filters: {},
  };

  // Inject type options into initial rows
  const initialRowsBuilt = useMemo(
    () =>
      initialFields.map((f) => {
        const row = fieldToRow(f);
        (row.field_type as any).cellVariant.options = typeOptions;
        return row;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [rows, setRows] = useState<GridRow[]>(initialRowsBuilt);

  // Remount the grid only when a row's field_type changes (so we can refresh
  // each cell's `disabled` state). Edits within a single field_type value
  // preserve grid state — drag, focus, in-flight edits — by keeping the same key.
  const remountKey = useMemo(
    () => rows.map((r) => String(extractRaw(r.field_type))).join("|"),
    [rows]
  );

  const handleChange = useCallback(
    (next: GridRow[]) => {
      const adjusted = applyConditionalDisabled(next);
      setRows(adjusted);
      const el = document.getElementById(hiddenFieldId);
      if (el) {
        const fields = adjusted.map(rowToField).filter(Boolean);
        (el as HTMLInputElement).value = JSON.stringify(fields);
      }
    },
    [hiddenFieldId]
  );

  const createRow = useCallback((): GridRow => {
    return {
      id: `field-${nextId++}`,
      label: {
        rawValue: "",
        displayValue: "",
        cellVariant: { type: "input" as const, placeholder: "Display name" },
      },
      field_type: {
        rawValue: "text",
        displayValue: "text",
        cellVariant: { type: "select" as const, options: typeOptions },
      },
      required: {
        rawValue: false,
        displayValue: "No",
        cellVariant: { type: "checkbox_toggle" as const },
      },
      options: {
        rawValue: "",
        displayValue: "",
        cellVariant: {
          type: "input" as const,
          placeholder: "Comma-separated",
          disabled: true,
        },
      },
      multiple: {
        rawValue: false,
        displayValue: "No",
        cellVariant: { type: "checkbox_toggle" as const, disabled: true },
      },
      max_files: {
        rawValue: String(DEFAULT_MAX_FILES),
        displayValue: String(DEFAULT_MAX_FILES),
        cellVariant: { type: "input" as const, placeholder: "1-10", disabled: true },
      },
      accept: {
        rawValue: DEFAULT_ACCEPT.join(", "),
        displayValue: DEFAULT_ACCEPT.join(", "),
        cellVariant: {
          type: "input" as const,
          placeholder: "Comma-separated MIME types",
          disabled: true,
        },
      },
    };
  }, [typeOptions]);

  return (
    <div className="field-schema-builder">
      <div className="field-schema-builder__heading">
        <div>
          <div className="field-schema-builder__title">{heading}</div>
          {description && <div className="field-schema-builder__description">{description}</div>}
        </div>
      </div>
      <div className="field-schema-builder__scroll">
        <LocalEditableGrid
          key={remountKey}
          grid={grid}
          initialRows={rows}
          onChange={handleChange}
          createRow={createRow}
          addLabel={addLabel}
          emptyHeading={emptyHeading}
          emptyDescription={emptyDescription}
        />
      </div>
    </div>
  );
};
